import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { InnerScrollCarousel } from 'dibs-scroll-carousel/exports/InnerScrollCarousel.ts';
import { ScrollCarouselProvider } from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { getSrcsetUrl, getSrcSetStringXDescriptors } from 'dibs-image-utils/exports/srcSet.ts';

import { BASE_IMAGE_URL } from '../tradeBenefitsConstants.ts';
import { trackTradeBenefitsEvent } from '../tradeBenefitsTracking.ts';

import styles from './TradeBenefitsTestimonialCarousel.scss';
import dibsCss from 'dibs-css';

const AVATAR_WIDTH = 82;

type TestimonialItem = {
    imagePath: string;
    quote: ReactNode;
    attribution: string;
};

const testimonials: TestimonialItem[] = [
    {
        imagePath: `${BASE_IMAGE_URL}/jeremiah_brent_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.jeremiahBrentDesign"
                defaultMessage="“1stDibs provides such effortless access to pieces for all design aesthetics — it’s one of our favorite treasure hunts. Somehow there’s always something new to find. It’s where we find those truly bespoke pieces that make every project feel personal.”"
            />
        ),
        attribution: 'Jeremiah Brent Design',
    },
    {
        imagePath: `${BASE_IMAGE_URL}/hines_collective_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.hinesCollective"
                defaultMessage="“Our Account Representative at 1stDibs has provided dedicated, tailored communication when we needed it most. They have been fast and responsive in addressing our outstanding needs to ensure they are met, and to ensure we receive superior customer service every step of the way.”"
            />
        ),
        attribution: 'Hines Collective',
    },
    {
        imagePath: `${BASE_IMAGE_URL}/nicole_hollis_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.nicoleHollis"
                defaultMessage="“Having personalized sourcing service with 1stDibs has enhanced our sourcing experience by offering recommendations and support for our specific searches. Access to exclusive or early listings has also provided unique opportunities that might not be available otherwise.”"
            />
        ),
        attribution: 'NICOLEHOLLIS',
    },
    {
        imagePath: `${BASE_IMAGE_URL}/shawn_henderson_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.shawnHendersonDesign"
                defaultMessage="“1stDibs makes the purchase process simple and seamless and there’s great support (should you need it). Searching for something is easy and intuitive with an incredible array of finds.”"
            />
        ),
        attribution: 'Shawn Henderson Design',
    },
    {
        imagePath: `${BASE_IMAGE_URL}/damon_liss_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.damonLissDesign"
                defaultMessage="“1stDibs is a great way to access trusted artisans and vendors all in one simple platform. This allows for sourcing and procurement to become seamless and facilitates the entire interior design process.”"
            />
        ),
        attribution: 'Damon Liss Design',
    },
    {
        imagePath: `${BASE_IMAGE_URL}/fern_santini_testimonial.jpg`,
        quote: (
            <FormattedMessage
                id="abt.TradeBenefitsTestimonialCarousel.quote.fernSantini"
                defaultMessage="“1stDibs is the magic that runs throughout everything I do. The best selections backed by people who have my back in case of damage or shipping issues.”"
            />
        ),
        attribution: 'Fern Santini, Inc.',
    },
];

const TradeBenefitsTestimonialCarouselItem: FC<TestimonialItem> = ({
    imagePath,
    quote,
    attribution,
}) => {
    return (
        <div className={classnames(dibsCss.flex, dibsCss.gapSmall, dibsCss.itemsStart)}>
            <div className={classnames(styles.image, dibsCss.roundedFull, dibsCss.overflowHidden)}>
                <img
                    src={getSrcsetUrl(imagePath, AVATAR_WIDTH)}
                    srcSet={
                        getSrcSetStringXDescriptors({
                            url: imagePath,
                            width: AVATAR_WIDTH,
                        }) ?? undefined
                    }
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className={classnames(dibsCss.hFull, dibsCss.wFull, dibsCss.objectCover)}
                />
            </div>
            <div>
                <div className={dibsCss.textBodySmallNormal}>{quote}</div>
                <div className={classnames(dibsCss.sassyFontBodyMediumHeavy, dibsCss.mtSmall)}>
                    — {attribution}
                </div>
            </div>
        </div>
    );
};

const TradeBenefitsTestimonialCarousel: FC = () => {
    const handleSlideChange = (): void => {
        trackTradeBenefitsEvent({
            stepInteractionName: 'testimonial scrolled',
            trigger: 'testimonials',
        });
    };

    return (
        <div
            className={classnames(
                dibsCss.bgWhite,
                dibsCss.roundedXsmall,
                dibsCss.ptMedium,
                dibsCss.pbSmall,
                dibsCss.pxXsmall
            )}
        >
            <ClientSuspense
                fallback={<TradeBenefitsTestimonialCarouselItem {...testimonials[0]} />}
            >
                <ScrollCarouselProvider
                    totalItems={testimonials.length}
                    isInfinite
                    onSlideChange={handleSlideChange}
                >
                    <InnerScrollCarousel showArrows orientation="horizontal" itemsToShow={1}>
                        {testimonials.map(testimonial => (
                            <TradeBenefitsTestimonialCarouselItem
                                key={testimonial.attribution}
                                {...testimonial}
                            />
                        ))}
                    </InnerScrollCarousel>
                </ScrollCarouselProvider>
            </ClientSuspense>
        </div>
    );
};

export { TradeBenefitsTestimonialCarousel };
